import { useEffect, useState } from "react";
import { api, type BillingPricing, type BillingState } from "../api";
import { creditCostForPlatforms, formatCredits, PLATFORM_CREDIT_COST } from "../lib/credits";

/** Credits for one post to ALL platforms — turns an abstract credit balance into
 * "≈ N posts" so a plan's value is legible. Kept in lockstep with the cost map. */
const ALL_PLATFORM_COST = creditCostForPlatforms(Object.keys(PLATFORM_CREDIT_COST));
function postsEstimate(credits: number): number {
  return Math.max(1, Math.round(credits / ALL_PLATFORM_COST));
}

/** One-line value prop per tier (beyond the raw credit number). */
const PLAN_BLURB: Record<string, string> = {
  starter: "Post daily across every platform.",
  pro: "More volume + room to grow.",
  agency: "Multiple brands, high volume.",
};

/** A short, friendly line under the plan tag: when the next charge lands, or a
 * plain-English state — never the raw Stripe status token. */
function planSubline(state: BillingState): string {
  const iso = state.currentPeriodEnd;
  const d = iso ? new Date(iso) : null;
  const date =
    d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
      : null;
  switch (state.status) {
    case "past_due":
    case "unpaid":
      return "Payment failed — update your card to keep publishing.";
    case "canceled":
      return date ? `Canceled — active until ${date}.` : "Canceled.";
    case "trialing":
      return date ? `Free trial — ends ${date}.` : "Free trial.";
    case "active":
      return date ? `Renews ${date}.` : "Active.";
    default:
      return date ? `Renews ${date}.` : "";
  }
}

const PLAN_LABEL: Record<string, string> = {
  starter: "Creator",
  pro: "Pro",
  agency: "Agency",
};

// Static fallback pricing, used only when the server doesn't report a `pricing`
// object (older server). Mirrors the fixed server-side pricing.
const FALLBACK_PRICING: BillingPricing = {
  starter: { priceLabel: "$9", credits: 4_500 },
  pro: { priceLabel: "$24", credits: 12_800 },
  agency: { priceLabel: "$59", credits: 32_500 },
  topup: { priceLabel: "$6", credits: 3_200 },
};

export function Billing() {
  const [state, setState] = useState<BillingState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Distinct from `error` (an action failure) and from a clean unconfigured
  // state: a load FAILURE must show a retry, not silently render nothing — which
  // looks identical to "billing is off" (#9).
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = () => {
    setLoadError(null);
    api
      .billing()
      .then(setState)
      .catch((e: unknown) =>
        setLoadError(e instanceof Error ? e.message : "Couldn't load billing."),
      );
  };

  useEffect(load, []);

  // Redirect the browser to a Stripe-hosted URL for checkout/portal flows.
  const go = async (fn: () => Promise<{ url: string }>) => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await fn();
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setBusy(false);
    }
  };

  const toggleAutoTopup = async (enabled: boolean) => {
    setError(null);
    try {
      await api.setAutoTopup(enabled);
      setState((s) => (s ? { ...s, autoTopup: enabled } : s));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update auto top-up.");
    }
  };

  // Load failed — offer a retry instead of a blank/absent card (#9).
  if (loadError) {
    return (
      <div className="card">
        <h2>Billing</h2>
        <p className="error">{loadError}</p>
        <button type="button" className="btn small" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  // Still loading (no data, no error yet).
  if (!state) {
    return (
      <div className="card">
        <h2>Billing</h2>
        <p className="muted small">Loading…</p>
      </div>
    );
  }

  if (!state.configured) {
    return (
      <div className="card">
        <h2>Billing</h2>
        <p className="muted small">Billing isn't enabled yet.</p>
      </div>
    );
  }

  // Prices/credit counts come from the server when it reports them, so the UI
  // never hardcodes "$9/$24/$6" (#5). Falls back to static copy on older servers.
  const pricing = state.pricing ?? FALLBACK_PRICING;

  return (
    <div className="card">
      <h2>Billing</h2>

      <div className="billing-summary">
        <div>
          <div className="billing-credits">{formatCredits(state.creditsBalance)}</div>
          <div className="muted small">credits · each post spends more on pricier platforms</div>
        </div>
        <div className="billing-plan">
          {state.plan ? (
            <>
              <span className="tag">{PLAN_LABEL[state.plan] ?? state.plan}</span>
              <div className="muted small">{planSubline(state)}</div>
            </>
          ) : (
            <>
              <span className="tag">Free plan</span>
              <div className="muted small">Upgrade for more monthly credits and brands.</div>
            </>
          )}
        </div>
      </div>

      <div className="plan-cards">
        {(
          [
            { key: "starter", name: "Creator", p: pricing.starter, popular: false },
            { key: "pro", name: "Pro", p: pricing.pro, popular: true },
            { key: "agency", name: "Agency", p: pricing.agency, popular: false },
          ] as const
        ).map(({ key, name, p, popular }) => {
          const current = state.plan === key;
          return (
            <div key={key} className={`plan-card${popular ? " popular" : ""}${current ? " current" : ""}`}>
              {popular && <span className="plan-badge">Most popular</span>}
              <div className="plan-name">{name}</div>
              <div className="plan-price">
                {p.priceLabel}
                <span className="muted small">/mo</span>
              </div>
              <div className="plan-credits">{formatCredits(p.credits)} credits/mo</div>
              <div className="muted small plan-posts">
                ≈ {postsEstimate(p.credits)} posts to all platforms
              </div>
              <div className="muted small plan-blurb">{PLAN_BLURB[key]}</div>
              <button
                className={`${popular && !current ? "btn primary" : "btn"} small plan-cta`}
                disabled={busy || current}
                onClick={() => go(() => api.checkout(key))}
              >
                {current ? "Current plan" : `Choose ${name}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn small grow" disabled={busy} onClick={() => go(api.topup)}>
          Buy credits · {pricing.topup.priceLabel}
          <span className="muted small"> · {formatCredits(pricing.topup.credits)} credits</span>
        </button>
        <button
          className="ghost small grow"
          disabled={busy}
          onClick={() => go(api.billingPortal)}
        >
          Manage billing
        </button>
      </div>

      <label className="billing-toggle">
        <input
          type="checkbox"
          checked={state.autoTopup}
          onChange={(e) => toggleAutoTopup(e.target.checked)}
        />
        <span>
          Auto top-up {pricing.topup.priceLabel} ({formatCredits(pricing.topup.credits)} credits)
          when you run out
        </span>
      </label>

      {error && <p className="error">{error}</p>}
    </div>
  );
}
